import React, { useState, useMemo } from 'react';
import { X, ArrowLeftRight, AlertTriangle, Check, Minus } from 'lucide-react';
import { useWizard } from '../../context/WizardContext';
import { designStorageService } from '../../utils/designStorageService';

const ComparisonMode = ({ isOpen, onClose }) => {
  const { savedDesigns } = useWizard();
  const [leftDesignId, setLeftDesignId] = useState('');
  const [rightDesignId, setRightDesignId] = useState('');

  const leftDesign = leftDesignId ? designStorageService.getDesignById(leftDesignId) : null;
  const rightDesign = rightDesignId ? designStorageService.getDesignById(rightDesignId) : null;

  const getLabel = (key, value) => {
    const labels = {
      philosophy: {
        positivism: 'Positivism',
        interpretivism: 'Interpretivism',
        pragmatism: 'Pragmatism',
        criticalRealism: 'Critical Realism',
        transformative: 'Transformative'
      },
      theoryApproach: {
        deductive: 'Deductive',
        inductive: 'Inductive',
        abductive: 'Abductive'
      },
      methodologicalChoice: {
        quantitative: 'Quantitative',
        qualitative: 'Qualitative',
        mixedMethods: 'Mixed Methods',
        multiMethod: 'Multi-Method'
      },
      strategy: {
        experiment: 'Experimental',
        survey: 'Survey',
        caseStudy: 'Case Study',
        ethnography: 'Ethnography',
        groundedTheory: 'Grounded Theory',
        phenomenology: 'Phenomenology',
        actionResearch: 'Action Research',
        narrativeInquiry: 'Narrative Inquiry'
      },
      timeHorizon: {
        crossSectional: 'Cross-sectional',
        longitudinal: 'Longitudinal'
      },
      ontology: {
        realism: 'Realism',
        relativism: 'Relativism',
        historicalRealism: 'Historical Realism'
      },
      epistemology: {
        objectivism: 'Objectivism',
        subjectivism: 'Subjectivism',
        constructionism: 'Constructionism'
      },
      axiology: {
        valueFree: 'Value-free',
        valueLaden: 'Value-laden',
        valueBalanced: 'Value-balanced'
      }
    };
    
    if (labels[key] && labels[key][value]) {
      return labels[key][value];
    }
    return value || 'Not selected';
  };

  const formatArray = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return 'None selected';
    return arr.join(', ');
  };

  const comparisonFields = [
    { section: 'Philosophical Dimensions', fields: [
      { key: 'philosophicalDimensions.ontology', label: 'Ontology', type: 'ontology' },
      { key: 'philosophicalDimensions.epistemology', label: 'Epistemology', type: 'epistemology' },
      { key: 'philosophicalDimensions.axiology', label: 'Axiology', type: 'axiology' }
    ]},
    { section: 'Research Paradigm & Approach', fields: [
      { key: 'philosophy', label: 'Philosophy/Paradigm', type: 'philosophy' },
      { key: 'theoryApproach', label: 'Theory Approach', type: 'theoryApproach' }
    ]},
    { section: 'Methodology', fields: [
      { key: 'methodologicalChoice', label: 'Methodological Choice', type: 'methodologicalChoice' },
      { key: 'strategy', label: 'Research Strategy', type: 'strategy' },
      { key: 'timeHorizon', label: 'Time Horizon', type: 'timeHorizon' }
    ]},
    { section: 'Data Collection', fields: [
      { key: 'techniques.dataCollection', label: 'Data Collection Methods', type: 'array' },
      { key: 'techniques.sampling', label: 'Sampling Strategy', type: 'array' },
      { key: 'techniques.analysis', label: 'Analysis Methods', type: 'array' },
      { key: 'techniques.qualityCriteria', label: 'Quality Criteria', type: 'array' }
    ]}
  ];

  const getValue = (design, keyPath) => {
    if (!design?.wizardAData) return null;
    const keys = keyPath.split('.');
    let value = design.wizardAData;
    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        return null;
      }
    }
    return value;
  };

  const renderValue = (value, type) => {
    if (type === 'array') {
      if (Array.isArray(value)) {
        return formatArray(value);
      }
      return value || 'None selected';
    }
    return getLabel(type, value);
  };

  const isDifferent = (leftVal, rightVal) => {
    if (Array.isArray(leftVal) && Array.isArray(rightVal)) {
      return JSON.stringify(leftVal.sort()) !== JSON.stringify(rightVal.sort());
    }
    return leftVal !== rightVal;
  };

  const comparisonStats = useMemo(() => {
    if (!leftDesign || !rightDesign) return { same: 0, different: 0 };
    
    let same = 0;
    let different = 0;
    
    comparisonFields.forEach(section => {
      section.fields.forEach(field => {
        const leftVal = getValue(leftDesign, field.key);
        const rightVal = getValue(rightDesign, field.key);
        if (isDifferent(leftVal, rightVal)) {
          different++;
        } else if (leftVal || rightVal) {
          same++;
        }
      });
    });
    
    return { same, different };
  }, [leftDesign, rightDesign, comparisonFields]);

  if (!isOpen) return null;

  return (
    <div className="comparison-overlay" onClick={onClose}>
      <div className="comparison-modal" onClick={e => e.stopPropagation()}>
        <div className="comparison-header">
          <h2><ArrowLeftRight size={24} /> Compare Research Designs</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="comparison-selectors">
          <div className="selector-group">
            <label>Design A:</label>
            <select 
              value={leftDesignId} 
              onChange={(e) => setLeftDesignId(e.target.value)}
              className="design-selector"
            >
              <option value="">Select a design...</option>
              {savedDesigns.map(design => (
                <option key={design.id} value={design.id} disabled={design.id === rightDesignId}>
                  {design.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="swap-icon">
            <ArrowLeftRight size={20} />
          </div>
          
          <div className="selector-group">
            <label>Design B:</label>
            <select 
              value={rightDesignId} 
              onChange={(e) => setRightDesignId(e.target.value)}
              className="design-selector"
            >
              <option value="">Select a design...</option>
              {savedDesigns.map(design => (
                <option key={design.id} value={design.id} disabled={design.id === leftDesignId}>
                  {design.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {leftDesign && rightDesign && (
          <div className="comparison-stats">
            <span className="stat same">
              <Check size={16} /> {comparisonStats.same} Same
            </span>
            <span className="stat different">
              <AlertTriangle size={16} /> {comparisonStats.different} Different
            </span>
          </div>
        )}

        {savedDesigns.length < 2 ? (
          <div className="comparison-empty">
            <AlertTriangle size={48} />
            <h3>Need More Designs</h3>
            <p>You need at least 2 saved designs to compare. Save your current design and create another to use comparison mode.</p>
          </div>
        ) : !leftDesign || !rightDesign ? (
          <div className="comparison-empty">
            <ArrowLeftRight size={48} />
            <h3>Select Two Designs</h3>
            <p>Choose two designs above to see a side-by-side comparison of their research methodology choices.</p>
          </div>
        ) : (
          <div className="comparison-table-container">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th className="field-col">Field</th>
                  <th className="design-col">{leftDesign.name}</th>
                  <th className="status-col">Match</th>
                  <th className="design-col">{rightDesign.name}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFields.map(section => (
                  <React.Fragment key={section.section}>
                    <tr className="section-row">
                      <td colSpan={4}>{section.section}</td>
                    </tr>
                    {section.fields.map(field => {
                      const leftVal = getValue(leftDesign, field.key);
                      const rightVal = getValue(rightDesign, field.key);
                      const different = isDifferent(leftVal, rightVal);
                      
                      return (
                        <tr key={field.key} className={different ? 'different' : 'same'}>
                          <td className="field-name">{field.label}</td>
                          <td className="value-cell left">{renderValue(leftVal, field.type)}</td>
                          <td className="match-cell">
                            {different ? (
                              <span className="diff-indicator different">
                                <AlertTriangle size={14} />
                              </span>
                            ) : (leftVal || rightVal) ? (
                              <span className="diff-indicator same">
                                <Check size={14} />
                              </span>
                            ) : (
                              <span className="diff-indicator empty">
                                <Minus size={14} />
                              </span>
                            )}
                          </td>
                          <td className="value-cell right">{renderValue(rightVal, field.type)}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonMode;
